// The two display names and what stands in for each where a profile carries
// none (02-DESIGN/03-console/01-the-console.md § signing in; identity
// 01-tenants-and-roles.md § the account, § the trust configuration): the slug
// as the workspace's name, the issuer's host as the provider's. Every page
// reads them from here. The platform's sign-in is not one of them: it is
// *Impire* on every page, whatever product the install runs beneath it
// (decision 0049; 01-the-console.md § the words).

import type { SignInRecord } from "../wire/auth.ts";
import type { Provider } from "./profile.ts";

/**
 * workspaceName is what a page calls the tenant: its display name — from its
 * profile or its membership — when it has one, its slug where it has none.
 */
export function workspaceName(slug: string, name?: string): string {
  const trimmed = (name ?? "").trim();
  return trimmed === "" ? slug : trimmed;
}

/**
 * PLATFORM_NAME is what every page calls the platform's sign-in — *Continue
 * with Impire*, *Impire sign-in* — written here and never read from the
 * platform profile, so the product beneath it is named on no page.
 */
export const PLATFORM_NAME = "Impire";

/** PLATFORM_SIGN_IN is the platform's sign-in as a thing, beside *Acme's own sign-in*. */
export const PLATFORM_SIGN_IN = `${PLATFORM_NAME} sign-in`;

/** FALLBACK_PROVIDER is what a provider is called when its profile names nothing to call it by. */
export const FALLBACK_PROVIDER = "your provider";

/**
 * providerName is what a page calls a workspace's own provider: its display
 * name when the profile has one, *GitHub* on the github lane, and the
 * issuer's host otherwise. Never the platform's: that is PLATFORM_NAME.
 */
export function providerName(p: Provider | undefined): string {
  if (p === undefined) {
    return FALLBACK_PROVIDER;
  }
  const display = (p.display_name ?? "").trim();
  if (display !== "") {
    return display;
  }
  if (p.lane === "github") {
    return "GitHub";
  }
  const issuer = p.issuer ?? "";
  if (issuer === "") {
    return FALLBACK_PROVIDER;
  }
  try {
    return new URL(issuer).host;
  } catch {
    return issuer;
  }
}

/**
 * recordProvider names what a record signed in with: *Impire* for the platform's
 * sign-in, never the product beneath it (decision 0049), and a workspace's
 * own provider by its issuer's host.
 */
export function recordProvider(x: SignInRecord, platform: Provider): string {
  if (x.lane === "github") {
    return platform.lane === "github" ? PLATFORM_NAME : "GitHub";
  }
  if (x.lane !== "app" && x.lane !== "workload" && x.issuer !== undefined && x.issuer === platform.issuer) {
    return PLATFORM_NAME;
  }
  if (x.lane === "app") {
    return "an app's key";
  }
  if (x.lane === "workload") {
    return "a workload's token";
  }
  if (x.issuer !== undefined && x.issuer !== "") {
    try {
      return new URL(x.issuer).host;
    } catch {
      return x.issuer;
    }
  }
  return "";
}

/** consoleAddress is a workspace's address as a person reads it under a card: the host and the slug, no scheme. */
export function consoleAddress(origin: string, slug: string): string {
  let host: string;
  try {
    host = new URL(origin).host;
  } catch {
    host = origin;
  }
  return slug === "" ? host : `${host}/${slug}`;
}

/** initialOf is the one letter the person's avatar shows. */
export function initialOf(display: string): string {
  const first = display.trim().charAt(0);
  return first === "" ? "?" : first.toLocaleUpperCase();
}
